import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Header from "../components/Header";
import HeroSection from "../components/HeroSection";
import ServicesPageHero from "../components/ServicesPageHero";
import ServiceDetailContent from "../components/ServiceDetailContent";
import SubServiceDetail from "../components/SubServiceDetail";
import CategorySubServices from "../components/CategorySubServices";
import AIToolCategoryWorkspace from "../components/AIToolCategoryWorkspace";import Footer from "../components/Footer";
import PageLoader from "../components/PageLoader";
import BlogSection from "../components/blog/BlogSection";
import { CATEGORY_THEME } from "../components/blog/BlogCard";
import { fetchMenu, fetchSubServiceCards, fetchServiceCards } from "../api";

// "blogs" lists every article (""), "blogs-ai-tools" etc. list one category; any other page is null
function blogCategoryOf(slug) {
  if (slug === "blogs") return "";
  return Object.keys(CATEGORY_THEME).find((key) => CATEGORY_THEME[key].page === slug) ?? null;
}

export default function PageView() {
  const { slug } = useParams();
  const blogCategory = blogCategoryOf(slug);
  const [ready, setReady] = useState(false); // this slug's real DB content (not just the menu match) has arrived
  const [menuItem, setMenuItem] = useState(undefined); // undefined = loading, null = not found
  const [subService, setSubService] = useState(null); // admin-authored detail content for this sub-service, if any
  const [parentService, setParentService] = useState(null); // this page's main service (ServiceCard) - itself for a category page, its parent for a sub-service page
  const [backTo, setBackTo] = useState("/#services"); // where the banner's "Back to ..." link goes
  const [backLabel, setBackLabel] = useState("Back to Services");
  const [backSlug, setBackSlug] = useState("services"); // SubServiceDetail's own "Back to {category}" link
  const [categoryChildren, setCategoryChildren] = useState([]); // sub-services under this category page, if any
  const [serviceCards, setServiceCards] = useState([]); // all ServiceCards, for a hub page's category-card images
  const [subServiceCards, setSubServiceCards] = useState([]); // for the sub-service card images on a category page
  const [hubChildren, setHubChildren] = useState([]); // a top-level dropdown's own categories (e.g. AI Tools' 6 categories)
  const [isAiTools, setIsAiTools] = useState(false); // this page's hub is "AI Tools", not "Services"
  const [redirectTo, setRedirectTo] = useState(null); // an AI Tools leaf page redirects into its category's workspace

  // A category page (Web Development) and a hub page (AI Tools) have no other component setting
  // the page title - a sub-service page's own SubServiceDetail already sets it, and HeroSection
  // sets its own for any other generic page.
  useEffect(() => {
    if (hubChildren.length > 0 && menuItem) {
      document.title = `${menuItem.title} - Mr. Aali Online Technologies`;
    } else if (parentService && !subService) {
      document.title = `${parentService.title} - Mr. Aali Online Technologies`;
    }
  }, [parentService, subService, hubChildren, menuItem]);

  useEffect(() => {
    // Guards every setState below - without it, a slow fetch from a PREVIOUS slug (e.g. a
    // redirect's async work still in flight) can land after the slug has already changed again,
    // re-setting stale state (redirectTo especially) and looping <Navigate> forever.
    let cancelled = false;

    setReady(false);
    setMenuItem(undefined);
    setSubService(null);
    setParentService(null);
    setCategoryChildren([]);
    setHubChildren([]);
    setIsAiTools(false);
    setRedirectTo(null);
    fetchMenu()
      .then((items) => {
        if (cancelled) return;
        const match = items.find((i) => i.slug === slug);
        setMenuItem(match || null);
        if (!match || slug === "services") {
          setReady(true);
          return;
        }

        // The Blogs page and its category pages are plain pages (banner + body, edited in the admin
        // under Pages), not Services-style category cards - so they fall through to HeroSection.
        const parentItem = items.find((i) => i.id === match.parent);
        if (match.slug === "blogs" || parentItem?.slug === "blogs") {
          setReady(true);
          return;
        }

        // A top-level dropdown with its own children (e.g. "AI Tools") is a hub page, just like
        // "Services" - it lists its categories as cards instead of a generic hero-only page.
        if (match.level === 0) {
          const children = items.filter((i) => i.parent === match.id);
          if (children.length > 0) {
            setHubChildren(children);
            fetchServiceCards()
              .then((cards) => { if (!cancelled) setServiceCards(cards); })
              .catch(() => {})
              .finally(() => { if (!cancelled) setReady(true); });
          } else {
            setReady(true);
          }
          return;
        }

        // The "service" this page belongs to: itself for a level-1 category page (e.g. Web
        // Development), its parent for a level-2 sub-service page (e.g. CorePHP Development).
        const category = match.level === 1 ? match : items.find((i) => i.id === match.parent);
        // The top-level dropdown this category lives under (Services, AI Tools, ...) - the
        // banner's "Back to ..." link goes there, matching whichever hub the page belongs to.
        const hub = category ? items.find((i) => i.id === category.parent) : null;
        if (hub) {
          setBackTo(hub.slug === "services" ? "/#services" : `/${hub.slug}`);
          setBackLabel(`Back to ${hub.title}`);
          setIsAiTools(hub.title === "AI Tools");
        }
        if (category) setBackSlug(match.level === 1 ? "services" : category.slug);

        // An AI Tools leaf page (e.g. AI Document Summarization) has no detail page of its own -
        // its "tool" lives as a button in its category's workspace, so redirect there instead.
        if (hub && hub.title === "AI Tools" && match.level === 2 && category) {
          setRedirectTo(`/${category.slug}?tool=${match.slug}`);
          return;
        }

        // A level-1 item shows all its sub-services in its own "Sub Services" list. A level-2
        // item shows its siblings as "Related Sub Services" (excluding itself).
        if (match.level === 1) {
          setCategoryChildren(items.filter((i) => i.parent === match.id));
        } else if (match.level === 2) {
          setCategoryChildren(items.filter((i) => i.parent === match.parent && i.id !== match.id));
        }

        // Sub-service leaf pages always use the SubServiceDetail design (matching the Service
        // Detail page's layout), whether or not admin-authored content exists yet for it.
        const fallbackSubService = () => ({
          title: category ? category.title : "",
          subtitle: match.title,
          how_to_develop: "",
          image: null,
        });

        const subServiceCardsDone = fetchSubServiceCards()
          .then((cards) => {
            if (cancelled) return;
            setSubServiceCards(cards);
            if (match.level === 2) {
              const found = cards.find((c) => c.subtitle === match.title);
              setSubService(found || fallbackSubService());
            }
          })
          .catch(() => {
            if (!cancelled && match.level === 2) setSubService(fallbackSubService());
          });

        // A category page (Web Development) and a sub-service page (CorePHP Development) both
        // show the main service's own banner (hero only, no rich detail content).
        const serviceCardsDone = category
          ? fetchServiceCards()
              .then((cards) => {
                if (cancelled) return;
                setServiceCards(cards);
                const found = cards.find((c) => c.title === category.title);
                // Always show the banner, even bare (title only), whether or not admin-authored
                // ServiceCard content exists yet for this category.
                setParentService(found || { title: category.title });
              })
              .catch(() => {})
          : Promise.resolve();

        Promise.allSettled([subServiceCardsDone, serviceCardsDone]).then(() => {
          if (!cancelled) setReady(true);
        });
      })
      .catch(() => { if (!cancelled) { setMenuItem(null); setReady(true); } });

    return () => { cancelled = true; };
  }, [slug]);

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  if (!ready) {
    return <PageLoader />;
  }

  if (menuItem === null) {
    return (
      <>
        <Header />
        <div className="h-16" />
        <div className="min-h-[60vh] flex flex-col items-center justify-center bg-navy text-white gap-4">
          <h1 className="text-3xl font-bold">Page Not Found</h1>
          <p className="text-gray-400">The page "{slug}" doesn't exist.</p>
          <Link to="/" className="px-6 py-2 rounded-full bg-brand-gradient text-white font-semibold">
            Back to Home
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="h-16" />
      {slug === "services" ? (
        <ServicesPageHero />
      ) : hubChildren.length > 0 ? (
        // A top-level dropdown hub (e.g. AI Tools): no separate hero - just its categories as
        // cards, each showing its own tools/icons (same idea as a Service Detail banner).
        <CategorySubServices children={hubChildren} serviceCards={serviceCards} heading={menuItem?.title || "Categories"} showTools />
      ) : subService ? (
        <>
          {parentService && (
            <ServiceDetailContent service={parentService} backTo={backTo} backLabel={backLabel} bannerOnly />
          )}
          <SubServiceDetail
            subService={subService}
            backSlug={backSlug}
            relatedItems={categoryChildren}
          />
        </>
      ) : parentService && isAiTools ? (
        // An AI Tools category (e.g. AI Document Tools): top container is the banner + a button
        // per tool, bottom container is that tool's workspace (built later, one tool at a time).
        <AIToolCategoryWorkspace category={parentService} tools={categoryChildren} />
      ) : parentService ? (
        // A category page (e.g. Web Development): just the service's banner, then an image-card
        // grid of its sub-services below - no rich detail content on this page.
        <>
          <ServiceDetailContent service={parentService} backTo={backTo} backLabel={backLabel} bannerOnly />
          <CategorySubServices children={categoryChildren} subServiceCards={subServiceCards} />
        </>
      ) : blogCategory !== null ? (
        // The Blogs page and its four category pages: the admin-edited banner, then the articles
        <>
          <HeroSection slug={slug} fallbackTitle={menuItem?.title} />
          <BlogSection category={blogCategory} />
        </>
      ) : (
        <HeroSection slug={slug} fallbackTitle={menuItem?.title} />
      )}
      <Footer />
    </>
  );
}
